import { memo, useCallback, useState } from 'react'
import { GS } from 'back-end-api'
import { useTranslation } from 'services/Translation'

import texts from './texts'
import { Panel, Heading, SubHeading, PrimaryButton, Notice } from './Styles'

// Task 15.2 — Onboarding CTA and payouts-unavailable state (Requirement 1.5).
//
// While the doctor's `bank_details_set` is false, render an onboarding
// call-to-action and a "payouts unavailable" indicator. Clicking the CTA calls
// GS.startWalletOnboarding({ return_url, refresh_url }) and redirects the
// browser to the returned link. Loading and error states are handled in place.
const Onboarding = () => {
  const { t } = useTranslation(texts)
  const [loading, setLoading] = useState(false)
  const [errored, setErrored] = useState(false)

  const startOnboarding = useCallback(async () => {
    setErrored(false)
    setLoading(true)
    try {
      // Return here after onboarding finishes; refresh sends the doctor back to
      // restart the flow if the link expires or is reused.
      const dashboardUrl = `${window.location.origin}${window.location.pathname}`
      const res = await GS.startWalletOnboarding({
        return_url: dashboardUrl,
        refresh_url: dashboardUrl,
      })

      const link = res?.data?.link
      if (res?.error || !link) {
        setErrored(true)
        setLoading(false)
        return
      }

      // Redirect the browser to the Stripe-hosted onboarding link.
      window.location.href = link
    } catch {
      setErrored(true)
      setLoading(false)
    }
  }, [])

  return (
    <Panel data-cy="walletOnboarding">
      <Heading>{t('onboardingTitle')}</Heading>
      <SubHeading>{t('onboardingBody')}</SubHeading>

      <Notice variant="warning" data-cy="payoutsUnavailable">
        {t('payoutsUnavailable')}
      </Notice>

      {errored && (
        <Notice variant="error" data-cy="onboardingError">
          {t('onboardingError')}
        </Notice>
      )}

      <PrimaryButton
        type="button"
        onClick={startOnboarding}
        disabled={loading}
        data-cy="onboardingCta"
      >
        {loading ? t('onboardingLoading') : t('onboardingCta')}
      </PrimaryButton>
    </Panel>
  )
}

Onboarding.whyDidYouRender = true

export default memo(Onboarding)
